import { html } from 'js-beautify';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  BlockManager,
  getChildIdx,
  getDynamicImageFieldKind,
  getDynamicImageFit,
  getListingClassId,
  getPageIdx,
} from '@core/utils';
import { JsonToMjmlOption } from './isProductionMode';
import React, { useContext } from 'react';
import { IBlockData } from '@core/typings';
import { unescape } from 'he';

interface BlockNode {
  type?: string;
  data?: { value?: Record<string, any> };
  attributes?: Record<string, any>;
  children?: BlockNode[];
}

function escapeXmlText(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// For interpolation into a double-quoted XML attribute value (mj-selector's
// path="..."), where an unescaped `"` would break out of the attribute.
function escapeXmlAttribute(value: string): string {
  return escapeXmlText(value).replace(/"/g, '&quot;');
}

/**
 * mj-image's own attribute schema drops id/data-* attributes when MJML compiles
 * it, so setting them directly on the image block never survives to the output
 * <img>. mj-html-attributes + mj-selector is MJML's supported passthrough for
 * this, matched by the css-class Image/index.tsx already stamps with the
 * dynamic image's id. The backend send-time swap (campaignevents.cfc
 * resolveDynamicImageAttributes) reads data-dynamic-tag/data-fallback-src/
 * data-dynamic-fit straight off the compiled <img>, so those are the only
 * attributes re-attached here.
 *
 * idx is threaded through to mirror Image/index.tsx's img_dyn_${idx} fallback
 * id for blocks with no persisted dynamicImageId (pre-dates ensureDynamicImageId,
 * or content imported without going through the attribute panel) - without it,
 * this walk would silently skip those images even though Image/index.tsx still
 * stamps their <td> with that same fallback id.
 */
function buildDynamicImageHtmlAttributes(node: BlockNode | null | undefined, idx: string): string {
  if (!node || typeof node !== 'object') return '';

  const value = node.data?.value || {};
  const explicitId = String(value.dynamicImageId || '').trim();
  const tag = String(value.dynamicImageTag || '')
    .trim()
    .replace(/^\{\{|\}\}$/g, '')
    .trim();
  const id = explicitId || (tag ? `img_dyn_${idx.replace(/[^a-zA-Z0-9_-]/g, '_')}` : '');

  let selector = '';
  if (id && tag) {
    const fit = getDynamicImageFit(getDynamicImageFieldKind(tag));
    // Matches Image/index.tsx's own data-fallback-src value, except listing
    // merge tags in src are left unresolved here (this walk has no ancestor
    // context to resolve them against) - known gap for listing-attached
    // dynamic images with an unresolved {{listing.x}} src.
    const fallbackSrc = String(node.attributes?.src || '');
    selector =
      // mj-image puts css-class on the wrapping <td>, not the <img> itself - the
      // backend (campaignevents.cfc resolveDynamicImageAttributes) regex-matches
      // only <img ...> tags, so the descendant selector is required here; ".id"
      // alone attaches to the <td> and the backend never sees it.
      `<mj-selector path=".${escapeXmlAttribute(id)} img">` +
      `<mj-html-attribute name="data-dynamic-tag">${escapeXmlText(tag)}</mj-html-attribute>` +
      `<mj-html-attribute name="data-fallback-src">${escapeXmlText(fallbackSrc)}</mj-html-attribute>` +
      (fit
        ? `<mj-html-attribute name="data-dynamic-fit">${escapeXmlText(fit)}</mj-html-attribute>`
        : '') +
      `</mj-selector>`;
  }

  const childSelectors = Array.isArray(node.children)
    ? node.children
        .map((child, index) => buildDynamicImageHtmlAttributes(child, getChildIdx(idx, index)))
        .join('')
    : '';

  return selector + childSelectors;
}

const COLUMN_TYPES = ['column', 'advanced_column'];

/**
 * Same mj-html-attributes workaround as buildDynamicImageHtmlAttributes, for
 * a block that owns a listing itself rather than reading someone else's
 * dynamic image tag - the Custom Listing Wrapper (mj-wrapper), or a column
 * switched to "Listing" (mj-column, see isDynamicListingColumn in
 * listingScope.ts). Both attribute schemas drop data-* the same way mj-image
 * does; CustomListing/index.tsx and Column/index.tsx each stamp a matching
 * css-class on their own compiled element in production when they have a
 * binding, so no descendant is needed here - unlike the image case, the
 * class lands directly on that element.
 *
 * The Wrapper keys its class off idx (reliable - it's registered directly).
 * The Column keys off the listing id value instead: it can compile as
 * advanced_column, which re-renders the base column through
 * generateAdvancedLayoutBlock with idx hard-coded to null, so an idx-based
 * class there would never match this idx-based walk.
 */
function buildListingScopeHtmlAttributes(node: BlockNode | null | undefined, idx: string): string {
  if (!node || typeof node !== 'object') return '';

  let selector = '';
  const isListingColumn =
    COLUMN_TYPES.indexOf(node.type || '') !== -1 && Boolean(node.data?.value?.isDynamicListing);
  if (node.type === 'custom_listing_block' || isListingColumn) {
    const listingId = String(node.data?.value?.selectedListingId || '').trim();
    if (listingId) {
      const classId = isListingColumn ? getListingClassId(listingId) : getListingClassId(idx);
      selector =
        `<mj-selector path=".${escapeXmlAttribute(classId)}">` +
        `<mj-html-attribute name="data-listing-id">${escapeXmlText(listingId)}</mj-html-attribute>` +
        `</mj-selector>`;
    }
  }

  const childSelectors = Array.isArray(node.children)
    ? node.children
        .map((child, index) => buildListingScopeHtmlAttributes(child, getChildIdx(idx, index)))
        .join('')
    : '';

  return selector + childSelectors;
}

type EmailRenderProps = {
  children?: React.ReactNode;
  context?: IBlockData;
  dataSource?: Record<string, any>;
  mode: 'production' | 'testing';
};

const EmailRenderContext = React.createContext<EmailRenderProps>({} as any);

const EmailRenderProvider: React.FC<EmailRenderProps> = props => {
  return (
    <EmailRenderContext.Provider value={props}>
      {props.children}
    </EmailRenderContext.Provider>
  );
};

export function JsonToMjml(options: JsonToMjmlOption): string {
  const { data, beautify } = options;
  const block = BlockManager.getBlockByType(data.type);
  if (!block) {
    throw new Error(`Block ${data.type} not found`);
  }
  let mjmlString = unescape(
    renderToStaticMarkup(
      <EmailRenderProvider
        dataSource={options.dataSource}
        mode={options.mode}
        context={options.context}
      >
        {block.render(options)}
      </EmailRenderProvider>,
    ),
  );
  if (options.mode === 'production') {
    const selectors =
      buildDynamicImageHtmlAttributes(data as unknown as BlockNode, getPageIdx()) +
      buildListingScopeHtmlAttributes(data as unknown as BlockNode, getPageIdx());
    if (selectors) {
      mjmlString = mjmlString.replace(
        '<mj-head>',
        `<mj-head><mj-html-attributes>${selectors}</mj-html-attributes>`,
      );
    }
  }
  if (beautify) {
    return html(mjmlString, { indent_size: 2 });
  }
  return mjmlString;
}

export const useEmailRenderContext = () => {
  return useContext(EmailRenderContext);
};
